import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { passwordPolicyError, PASSWORD_HINT } from "@/lib/password";
import { Button } from "@/components/ui/button";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Key, ShieldCheck } from "@phosphor-icons/react";

export default function ForcePasswordChange() {
  const { refresh, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const policyErr = next ? passwordPolicyError(next) : null;

  const submit = async (e) => {
    e.preventDefault();
    const err = passwordPolicyError(next);
    if (err) return toast.error(err);
    if (next !== confirm) return toast.error("Las contraseñas no coinciden");
    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: current, new_password: next });
      if (data?.token) localStorage.setItem("inv_token", data.token);
      toast.success("Contraseña actualizada");
      await refresh();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
      <form onSubmit={submit} className="w-full max-w-md bg-white border border-zinc-200 p-8 space-y-5" data-testid="force-pw-form">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-[#002FA7]/10 rounded-sm">
            <Key size={24} color="#002FA7" weight="duotone" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">Cambia tu contraseña</h1>
            <p className="text-sm text-zinc-500">Por seguridad debes definir una nueva contraseña para continuar.</p>
          </div>
        </div>
        <div className="flex items-start gap-2 text-xs text-[#002FA7] bg-[#002FA7]/5 border border-[#002FA7]/20 p-3" data-testid="fpc-policy-hint">
          <ShieldCheck size={16} weight="duotone" className="shrink-0 mt-0.5" />
          <span>Nueva política: {PASSWORD_HINT} Al guardar se cerrarán tus otras sesiones.</span>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Contraseña actual</Label>
          <PasswordInput value={current} onChange={(e)=>setCurrent(e.target.value)} required data-testid="fpc-current" className="mt-1" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Nueva contraseña</Label>
          <PasswordInput value={next} onChange={(e)=>setNext(e.target.value)} required data-testid="fpc-new" className="mt-1" />
          {policyErr && <div className="text-[11px] text-[#E0000F] mt-1" data-testid="fpc-policy-error">{policyErr}</div>}
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Confirma nueva contraseña</Label>
          <PasswordInput value={confirm} onChange={(e)=>setConfirm(e.target.value)} required data-testid="fpc-confirm" className="mt-1" />
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={logout} data-testid="fpc-logout">Salir</Button>
          <Button type="submit" disabled={saving} className="flex-1 bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="fpc-submit">
            {saving ? "Guardando…" : "Actualizar contraseña"}
          </Button>
        </div>
      </form>
    </div>
  );
}
